import { queryOptions } from "@tanstack/react-query";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";

import type { Viewer } from "@/auth";
import { authMiddleware } from "@/auth/shared";
import { db } from "@/db/connection";
import { unauthorized } from "@/lib/responses";

export const getViewerProfilesHandler = createServerOnlyFn(
  async (viewerId: Viewer["id"]) => {
    // Get profiles through the user_profiles join table (new system)
    const linkedProfiles = await db.query.userProfiles.findMany({
      where: { userId: viewerId },
      with: {
        playerProfile: {
          with: {
            activeMembership: {
              columns: {
                id: true,
                tshirtSize: true,
              },
            },
            level: true,
          },
        },
      },
      orderBy: {
        createdAt: "asc",
      },
    });

    // Also get profiles directly linked via userId (legacy/direct ownership)
    const directProfiles = await db.query.playerProfiles.findMany({
      with: {
        activeMembership: {
          columns: {
            id: true,
            tshirtSize: true,
          },
        },
        level: true,
      },
      where: { userId: viewerId },
      orderBy: {
        createdAt: "asc",
      },
    });

    // Combine and deduplicate, preferring the user_profiles entry if both exist
    const profileMap = new Map<
      number,
      {
        role: "self" | "guardian";
        profile: (typeof directProfiles)[number];
      }
    >();

    // Add direct profiles first (default to "self" role)
    for (const profile of directProfiles) {
      profileMap.set(profile.id, { role: "self", profile });
    }

    // Override with user_profiles entries (which have explicit roles)
    for (const { role, playerProfile } of linkedProfiles) {
      profileMap.set(playerProfile.id, { role, profile: playerProfile });
    }

    // Return as array with role included
    return Array.from(profileMap.values()).map(({ role, profile }) => ({
      ...profile,
      role,
    }));
  },
);

const getViewerProfilesFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .handler(({ context: { viewer } }) => {
    if (!viewer) {
      throw unauthorized();
    }

    return getViewerProfilesHandler(viewer.id);
  });

export const getViewerProfilesQueryOptions = () =>
  queryOptions({
    queryKey: ["viewer_profiles"],
    queryFn: () => getViewerProfilesFn(),
  });
